<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import ImovelAPI from 'dashboard/api/imoveis';
import { useAlert } from 'dashboard/composables';
import { usePolicy } from 'dashboard/composables/usePolicy';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';
import {
  DUPLICATE_MATCH_OPTIONS,
  DUPLICATE_STATUS_OPTIONS,
  mergeActionError,
  mergePlanPayload,
  normalizeDuplicateQueue,
  normalizeMergeOperation,
} from '../utils/duplicateCandidates';

const { checkPermissions } = usePolicy();

const candidates = ref([]);
const meta = ref({
  count: 0,
  page: 1,
  perPage: 50,
  totalPages: 1,
  statusCounts: {},
  partial: false,
  partialCount: 0,
  partialMessage: '',
});
const selectedCandidateId = ref(null);
const selectedOperation = ref(null);
const statusFilter = ref('');
const matchTypeFilter = ref('');
const reviewReason = ref('');
const isLoading = ref(false);
const actionKey = ref('');
const loadError = ref('');
const actionMessage = ref('');
const conflictMessage = ref('');

const canManageMerges = computed(() => checkPermissions(['administrator']));
const hasCandidates = computed(() => candidates.value.length > 0);
const selectedCandidate = computed(
  () =>
    candidates.value.find(
      candidate => candidate.id === selectedCandidateId.value
    ) ||
    candidates.value[0] ||
    null
);
const activeOperation = computed(
  () => selectedOperation.value || selectedCandidate.value?.latestMergeOperation
);
const requiresReason = computed(() => !reviewReason.value.trim());
const canPlanOrApply = computed(
  () =>
    canManageMerges.value &&
    selectedCandidate.value &&
    !selectedCandidate.value.partial &&
    ['pending', 'confirmed'].includes(selectedCandidate.value.status) &&
    !requiresReason.value
);
const canRevert = computed(
  () =>
    canManageMerges.value &&
    activeOperation.value?.status === 'applied' &&
    activeOperation.value?.reversible &&
    !requiresReason.value
);
const canReject = computed(
  () =>
    canManageMerges.value &&
    selectedCandidate.value &&
    ['pending', 'confirmed'].includes(selectedCandidate.value.status) &&
    !requiresReason.value
);
const summaryItems = computed(() => [
  {
    label: 'Pendentes',
    value: meta.value.statusCounts.pending || 0,
    tone: 'warning',
  },
  {
    label: 'Mesclados',
    value: meta.value.statusCounts.merged || 0,
    tone: 'positive',
  },
  {
    label: 'Parciais',
    value: meta.value.partialCount || 0,
    tone: meta.value.partial ? 'danger' : 'neutral',
  },
]);

const statusTone = status =>
  ({
    pending: 'warning',
    confirmed: 'info',
    merged: 'positive',
    rejected: 'neutral',
  })[status] || 'neutral';

const operationTone = status =>
  ({
    planned: 'info',
    previewed: 'info',
    applied: 'positive',
    reverted: 'neutral',
    failed: 'danger',
  })[status] || 'neutral';

const formatDate = value => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const objectEntries = value => Object.entries(value || {});

const queryParams = () => ({
  status: statusFilter.value || undefined,
  match_type: matchTypeFilter.value || undefined,
  page: meta.value.page || 1,
  per_page: meta.value.perPage || 50,
});

const selectCandidate = candidate => {
  selectedCandidateId.value = candidate.id;
  selectedOperation.value = candidate.latestMergeOperation;
  actionMessage.value = '';
  conflictMessage.value = '';
};

const fetchCandidates = async () => {
  isLoading.value = true;
  loadError.value = '';
  try {
    const { data } = await ImovelAPI.duplicateCandidates(queryParams());
    const normalized = normalizeDuplicateQueue(data);
    candidates.value = normalized.candidates;
    meta.value = normalized.meta;
    if (!selectedCandidateId.value && candidates.value[0]) {
      selectCandidate(candidates.value[0]);
    }
  } catch {
    loadError.value = 'Nao foi possivel carregar candidatos de duplicidade.';
  } finally {
    isLoading.value = false;
  }
};

const applyFilters = () => {
  meta.value = { ...meta.value, page: 1 };
  selectedCandidateId.value = null;
  selectedOperation.value = null;
  fetchCandidates();
};

const changePage = direction => {
  const nextPage = Number(meta.value.page || 1) + direction;
  if (nextPage < 1 || nextPage > Number(meta.value.totalPages || 1)) return;
  meta.value = { ...meta.value, page: nextPage };
  selectedCandidateId.value = null;
  selectedOperation.value = null;
  fetchCandidates();
};

const ensureReason = () => {
  if (!requiresReason.value) return true;

  actionMessage.value = 'Informe o motivo da revisao antes de gravar.';
  return false;
};

const handleActionError = error => {
  const details = mergeActionError(error);
  const prefix = details.conflict ? 'Conflito 409 preservado: ' : '';
  conflictMessage.value = `${prefix}${details.message}`;
  useAlert(conflictMessage.value);
};

const planMerge = async () => {
  if (!selectedCandidate.value || !ensureReason()) return null;

  actionKey.value = `plan:${selectedCandidate.value.id}`;
  actionMessage.value = '';
  conflictMessage.value = '';
  try {
    const { data } = await ImovelAPI.planIdentityMerge(
      mergePlanPayload({
        candidate: selectedCandidate.value,
        reason: reviewReason.value.trim(),
      })
    );
    selectedOperation.value = normalizeMergeOperation(data?.data);
    actionMessage.value = 'Plano criado com preview reversivel.';
    return selectedOperation.value;
  } catch (error) {
    handleActionError(error);
    return null;
  } finally {
    actionKey.value = '';
  }
};

const applyMerge = async () => {
  if (!canPlanOrApply.value) {
    ensureReason();
    return;
  }

  const operation =
    activeOperation.value?.status === 'planned'
      ? activeOperation.value
      : await planMerge();
  if (!operation?.id) return;

  actionKey.value = `apply:${operation.id}`;
  try {
    const { data } = await ImovelAPI.applyIdentityMerge({
      merge_operation_id: operation.id,
    });
    selectedOperation.value = normalizeMergeOperation(data?.data);
    actionMessage.value = 'Merge aplicado e auditado.';
    useAlert(actionMessage.value);
    await fetchCandidates();
  } catch (error) {
    handleActionError(error);
  } finally {
    actionKey.value = '';
  }
};

const revertMerge = async () => {
  if (!activeOperation.value?.id || !ensureReason()) return;

  actionKey.value = `revert:${activeOperation.value.id}`;
  actionMessage.value = '';
  conflictMessage.value = '';
  try {
    const { data } = await ImovelAPI.revertIdentityMerge({
      merge_operation_id: activeOperation.value.id,
      reason: reviewReason.value.trim(),
    });
    selectedOperation.value = normalizeMergeOperation(data?.data);
    actionMessage.value = 'Merge revertido com motivo registrado.';
    useAlert(actionMessage.value);
    await fetchCandidates();
  } catch (error) {
    handleActionError(error);
  } finally {
    actionKey.value = '';
  }
};

const rejectCandidate = async () => {
  if (!selectedCandidate.value || !ensureReason()) return;

  actionKey.value = `reject:${selectedCandidate.value.id}`;
  actionMessage.value = '';
  conflictMessage.value = '';
  try {
    const { data } = await ImovelAPI.rejectDuplicateCandidate(
      selectedCandidate.value.id,
      { reason: reviewReason.value.trim() }
    );
    actionMessage.value = 'Candidato marcado como falso positivo.';
    useAlert(actionMessage.value);
    selectedCandidateId.value = data?.data?.id || selectedCandidateId.value;
    await fetchCandidates();
  } catch (error) {
    handleActionError(error);
  } finally {
    actionKey.value = '';
  }
};

watch(selectedCandidate, candidate => {
  selectedOperation.value = candidate?.latestMergeOperation || null;
});

onMounted(fetchCandidates);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <main class="flex h-full min-h-0 flex-col overflow-auto bg-n-background">
    <LaisHeader
      title="Duplicidades de imoveis"
      subtitle="Revise candidatos, aplique merge humano reversivel e preserve conflitos de operacao."
    />

    <section class="border-b border-n-weak px-4 py-4 sm:px-6">
      <div class="grid gap-3 md:grid-cols-[1fr_1fr_auto]">
        <label class="flex min-w-0 flex-col text-xs text-n-slate-11">
          Status
          <select
            v-model="statusFilter"
            class="mt-1 w-full min-w-0 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm"
          >
            <option
              v-for="option in DUPLICATE_STATUS_OPTIONS"
              :key="option.value || 'operational-status'"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>
        <label class="flex min-w-0 flex-col text-xs text-n-slate-11">
          Tipo de match
          <select
            v-model="matchTypeFilter"
            class="mt-1 w-full min-w-0 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm"
          >
            <option
              v-for="option in DUPLICATE_MATCH_OPTIONS"
              :key="option.value || 'all-match-types'"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>
        <div class="flex flex-col gap-2 sm:flex-row sm:items-end">
          <LaisPrimaryButton
            class="w-full sm:w-auto"
            icon="i-lucide-filter"
            @click="applyFilters"
          >
            Filtrar
          </LaisPrimaryButton>
          <LaisPrimaryButton
            class="w-full sm:w-auto"
            variant="ghost"
            icon="i-lucide-refresh-cw"
            :is-loading="isLoading"
            @click="fetchCandidates"
          >
            Atualizar
          </LaisPrimaryButton>
        </div>
      </div>
    </section>

    <section class="grid gap-3 px-4 py-4 sm:px-6 md:grid-cols-3">
      <LaisCard v-for="item in summaryItems" :key="item.label" class="p-4">
        <p class="text-xs text-n-slate-11">{{ item.label }}</p>
        <p class="mt-1 text-xl font-semibold text-n-slate-12">
          {{ item.value }}
        </p>
      </LaisCard>
    </section>

    <section
      v-if="meta.partial"
      class="mx-6 mb-4 rounded-lg border border-n-ruby-7 bg-n-ruby-3 px-4 py-3 text-sm text-n-ruby-11"
    >
      {{ meta.partialMessage }}
    </section>

    <section
      v-if="!canManageMerges"
      class="mx-4 mb-4 rounded-lg border border-n-amber-7 bg-n-amber-3 px-4 py-3 text-sm text-n-amber-11 sm:mx-6"
      role="status"
      aria-live="polite"
      data-test="duplicate-permission-state"
    >
      Sem permissao de administrador para planejar, aplicar, reverter ou
      rejeitar merges nesta fila.
    </section>

    <section
      class="grid min-h-0 flex-1 gap-4 px-4 pb-6 sm:px-6 xl:grid-cols-[minmax(0,1fr)_minmax(360px,0.85fr)]"
    >
      <div class="min-h-0 min-w-0 overflow-hidden">
        <p
          v-if="loadError"
          class="mb-3 text-sm text-n-ruby-11"
          role="alert"
          data-test="duplicate-error-state"
        >
          {{ loadError }}
        </p>
        <div
          v-if="isLoading"
          class="py-10 text-center text-sm text-n-slate-11"
          aria-live="polite"
        >
          Carregando candidatos...
        </div>
        <div
          v-else-if="!hasCandidates"
          class="py-10 text-center text-sm text-n-slate-11"
          data-test="duplicate-empty-state"
        >
          Nenhum candidato encontrado para os filtros atuais.
        </div>
        <div
          v-else
          class="overflow-x-auto rounded-lg border border-n-weak"
          data-test="duplicate-candidates-table-scroll"
        >
          <table
            class="min-w-[760px] w-full text-sm"
            data-test="duplicate-candidates-table"
          >
            <thead>
              <tr class="border-b border-n-weak text-left text-n-slate-11">
                <th class="py-2 pl-3">Candidato</th>
                <th>Score</th>
                <th>Status</th>
                <th>Operacao</th>
                <th class="pr-3" />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="candidate in candidates"
                :key="candidate.id"
                class="border-b border-n-weak align-top"
                :class="
                  selectedCandidate?.id === candidate.id ? 'bg-n-alpha-1' : ''
                "
              >
                <td class="py-3 pl-3">
                  <p class="font-medium text-n-slate-12">
                    #{{ candidate.id }} - {{ candidate.matchType }}
                  </p>
                  <p class="max-w-[28rem] break-words text-xs text-n-slate-11">
                    {{ candidate.primaryImovel.titulo }} x
                    {{ candidate.duplicateImovel.titulo }}
                  </p>
                  <p
                    v-if="candidate.partial"
                    class="mt-1 max-w-[28rem] break-words text-xs text-n-ruby-11"
                  >
                    Payload parcial: {{ candidate.partialReason }}
                  </p>
                </td>
                <td class="py-3 text-n-slate-11">
                  {{ candidate.matchScoreLabel }}
                </td>
                <td class="py-3">
                  <LaisStatusBadge
                    :label="candidate.status"
                    :tone="statusTone(candidate.status)"
                    dot
                  />
                </td>
                <td class="py-3">
                  <LaisStatusBadge
                    v-if="candidate.latestMergeOperation"
                    :label="candidate.latestMergeOperation.status"
                    :tone="operationTone(candidate.latestMergeOperation.status)"
                    dot
                  />
                  <span v-else class="text-xs text-n-slate-11">Sem plano</span>
                </td>
                <td class="py-3 pr-3 text-right">
                  <button
                    type="button"
                    class="whitespace-nowrap rounded-lg border border-n-weak px-2 py-1 text-xs hover:bg-n-alpha-1"
                    :aria-label="`Revisar candidato ${candidate.id}`"
                    @click="selectCandidate(candidate)"
                  >
                    Revisar
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="mt-4 flex items-center justify-between text-sm">
          <button
            class="rounded-lg border border-n-weak px-3 py-1.5 disabled:opacity-50"
            :disabled="meta.page <= 1"
            @click="changePage(-1)"
          >
            Anterior
          </button>
          <span class="text-n-slate-11">
            Pagina {{ meta.page }} de {{ meta.totalPages }}
          </span>
          <button
            class="rounded-lg border border-n-weak px-3 py-1.5 disabled:opacity-50"
            :disabled="meta.page >= meta.totalPages"
            @click="changePage(1)"
          >
            Proxima
          </button>
        </div>
      </div>

      <aside class="flex min-w-0 flex-col gap-4">
        <LaisCard class="p-4">
          <label class="flex flex-col text-xs text-n-slate-11">
            Motivo da revisao
            <textarea
              v-model="reviewReason"
              rows="3"
              class="mt-1 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm"
              placeholder="Ex.: matricula e unidade conferidas no atendimento"
              :disabled="!canManageMerges"
            />
          </label>
          <p v-if="requiresReason" class="mt-2 text-xs text-n-amber-11">
            Merge, reversao e rejeicao exigem motivo.
          </p>
        </LaisCard>

        <LaisCard class="p-4">
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-sm font-semibold text-n-slate-12">
                Revisao selecionada
              </p>
              <p class="text-xs text-n-slate-11">
                {{
                  selectedCandidate
                    ? `Candidato #${selectedCandidate.id}`
                    : 'Selecione um candidato'
                }}
              </p>
            </div>
            <LaisStatusBadge
              v-if="selectedCandidate"
              :label="selectedCandidate.status"
              :tone="statusTone(selectedCandidate.status)"
              dot
            />
          </div>

          <div v-if="selectedCandidate" class="mt-4 flex flex-col gap-3">
            <div class="grid gap-3 text-xs text-n-slate-11 md:grid-cols-2">
              <div class="min-w-0 rounded-lg border border-n-weak p-3">
                <p class="font-semibold text-n-slate-12">Primario</p>
                <p class="mt-1 break-words">
                  {{ selectedCandidate.primaryImovel.titulo }}
                </p>
                <p class="break-words">
                  {{ selectedCandidate.primaryImovel.endereco || '-' }}
                </p>
                <p class="break-words">
                  {{ selectedCandidate.primaryImovel.bairro }}
                  {{ selectedCandidate.primaryImovel.cidade }}
                </p>
              </div>
              <div class="min-w-0 rounded-lg border border-n-weak p-3">
                <p class="font-semibold text-n-slate-12">Duplicado</p>
                <p class="mt-1 break-words">
                  {{ selectedCandidate.duplicateImovel.titulo }}
                </p>
                <p class="break-words">
                  {{ selectedCandidate.duplicateImovel.endereco || '-' }}
                </p>
                <p class="break-words">
                  {{ selectedCandidate.duplicateImovel.bairro }}
                  {{ selectedCandidate.duplicateImovel.cidade }}
                </p>
              </div>
            </div>

            <div class="rounded-lg border border-n-weak p-3">
              <p class="text-xs font-semibold uppercase text-n-slate-11">
                Sinais
              </p>
              <div class="mt-2 flex flex-wrap gap-2">
                <span
                  v-for="[key, value] in objectEntries(
                    selectedCandidate.signals
                  )"
                  :key="`signal-${key}`"
                  class="max-w-full break-all rounded-md bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-11"
                >
                  {{ key }}: {{ value }}
                </span>
                <span
                  v-if="!objectEntries(selectedCandidate.signals).length"
                  class="text-xs text-n-slate-11"
                >
                  Sem sinais detalhados
                </span>
              </div>
            </div>

            <div class="rounded-lg border border-n-weak p-3">
              <p class="text-xs font-semibold uppercase text-n-slate-11">
                Discriminadores
              </p>
              <div class="mt-2 flex flex-wrap gap-2">
                <span
                  v-for="[key, value] in objectEntries(
                    selectedCandidate.discriminators
                  )"
                  :key="`disc-${key}`"
                  class="max-w-full break-all rounded-md bg-n-amber-3 px-2 py-1 text-xs text-n-amber-11"
                >
                  {{ key }}: {{ value }}
                </span>
                <span
                  v-if="!objectEntries(selectedCandidate.discriminators).length"
                  class="text-xs text-n-slate-11"
                >
                  Sem discriminadores
                </span>
              </div>
            </div>

            <div class="rounded-lg border border-n-weak p-3">
              <div class="flex items-center justify-between gap-3">
                <div>
                  <p class="text-xs font-semibold uppercase text-n-slate-11">
                    Plano de merge
                  </p>
                  <p class="text-sm text-n-slate-12">
                    {{
                      activeOperation
                        ? `Operacao #${activeOperation.id}`
                        : 'Nenhum plano criado'
                    }}
                  </p>
                </div>
                <LaisStatusBadge
                  v-if="activeOperation"
                  :label="activeOperation.status"
                  :tone="operationTone(activeOperation.status)"
                  dot
                />
              </div>
              <div
                v-if="activeOperation?.preview"
                class="mt-3 grid grid-cols-2 gap-2 text-xs text-n-slate-11"
              >
                <div>
                  <p>Referencias afetadas</p>
                  <p class="font-semibold text-n-slate-12">
                    {{
                      activeOperation.preview.affected_reference_total ||
                      activeOperation.preview.affectedReferenceTotal ||
                      0
                    }}
                  </p>
                </div>
                <div>
                  <p>Reversivel</p>
                  <p class="font-semibold text-n-slate-12">
                    {{ activeOperation.reversible ? 'Sim' : 'Nao' }}
                  </p>
                </div>
              </div>
              <p v-if="activeOperation?.revertReason" class="mt-2 text-xs">
                Revertido: {{ activeOperation.revertReason }}
              </p>
              <p v-if="activeOperation?.appliedAt" class="mt-2 text-xs">
                Aplicado em {{ formatDate(activeOperation.appliedAt) }}
              </p>
            </div>

            <p
              v-if="actionMessage"
              class="text-sm text-n-teal-11"
              aria-live="polite"
            >
              {{ actionMessage }}
            </p>
            <p
              v-if="conflictMessage"
              class="text-sm text-n-ruby-11"
              role="alert"
            >
              {{ conflictMessage }}
            </p>

            <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <LaisPrimaryButton
                class="w-full"
                variant="ghost"
                icon="i-lucide-file-check"
                :disabled="!canPlanOrApply || actionKey.startsWith('plan:')"
                @click="planMerge"
              >
                Planejar
              </LaisPrimaryButton>
              <LaisPrimaryButton
                class="w-full"
                icon="i-lucide-git-merge"
                :disabled="!canPlanOrApply || actionKey.startsWith('apply:')"
                @click="applyMerge"
              >
                Aplicar merge
              </LaisPrimaryButton>
              <LaisPrimaryButton
                class="w-full"
                variant="ghost"
                icon="i-lucide-undo-2"
                :disabled="!canRevert || actionKey.startsWith('revert:')"
                @click="revertMerge"
              >
                Reverter
              </LaisPrimaryButton>
              <LaisPrimaryButton
                class="w-full"
                variant="ghost"
                icon="i-lucide-ban"
                :disabled="!canReject || actionKey.startsWith('reject:')"
                @click="rejectCandidate"
              >
                Falso positivo
              </LaisPrimaryButton>
            </div>
          </div>
        </LaisCard>
      </aside>
    </section>
  </main>
</template>
